import { useMemo, useState } from "react";
import { Navigate } from "react-router";
import { Container, Tab, Tabs, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { eligibleCompanies, tierOn } from "src/utils/hallinta/companyRoles";
import CompanyBasicsCard from "src/components/hallinta/CompanyBasicsCard";
import PeopleTab from "src/components/hallinta/PeopleTab";
import VehiclesTab from "src/components/hallinta/VehiclesTab";
import SijainnitTab from "src/components/hallinta/SijainnitTab";

export default function Hallinta() {
  const { user, ownerAsiakasId } = useAuth();
  const t = useT();
  const companies = useMemo(() => eligibleCompanies(user), [user]);
  const [tabKey, setTabKey] = useState(null);

  // Hallinta reflects the active tenant — the company switcher lives in the account
  // menu (switching there re-mints the JWT, which updates ownerAsiakasId here). Fall
  // back to the first manageable company only if the active one isn't manageable.
  const company = useMemo(() => {
    if (!companies.length) return null;
    return companies.find((c) => c.asiakasId === ownerAsiakasId) || companies[0];
  }, [companies, ownerAsiakasId]);

  const tier = company ? tierOn(user, company.asiakasId) : null;
  const canEdit = tier === "edit" || tier === "admin";
  const isAdmin = tier === "admin";

  const em = company?.enabledModules || {};
  const tabs = useMemo(() => (company ? [
    em.henkilot && { key: "henkilot", label: t("provider.people.heading"), render: () => <PeopleTab company={company} canEdit={canEdit} isAdmin={isAdmin} /> },
    em.ajoneuvot && { key: "ajoneuvot", label: t("provider.vehicles.heading"), render: () => <VehiclesTab company={company} canEdit={canEdit} /> },
    em.sijainnit && { key: "sijainnit", label: t("provider.sijainnit.heading"), render: () => <SijainnitTab company={company} canEdit={canEdit} /> },
  ].filter(Boolean) : []), [company, em.henkilot, em.ajoneuvot, em.sijainnit, canEdit, isAdmin, t]);

  // Derive the active tab: keep the user's choice when still valid, else fall back to the first tab.
  const activeTab = useMemo(
    () => tabs.find((t) => t.key === tabKey) ?? tabs[0] ?? null,
    [tabs, tabKey],
  );

  if (!companies.length) return <Navigate to="/" replace />;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 5 } }}>
      <Typography sx={{ fontFamily: "'Black Ops One', serif", fontSize: 22, letterSpacing: "0.06em",
        textTransform: "uppercase", color: "text.primary", mb: 3 }}>
        {t("provider.hallinta.heading")}
      </Typography>

      {company && (
        <>
          {/* key by asiakasId so an impersonation swap / company switch remounts the
              card and re-seeds its local `row`. Without it the edit modal keeps the
              previous company's id and fetches it with the new token → 403. */}
          <CompanyBasicsCard key={company.asiakasId} company={company} canEdit={canEdit} />
          {tabs.length > 0 && (
            <Tabs value={activeTab?.key ?? false} onChange={(_, v) => setTabKey(v)} sx={{ mt: 3, mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
              {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>
          )}
          {activeTab?.render()}
        </>
      )}
    </Container>
  );
}
